import React, { useState, useEffect, useCallback } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { clientBackup } from '../../utils/clientBackup';
import { useAuth } from '../../contexts/AuthContext';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const { user, signInWithGoogle, logout, loading: authLoading } = useAuth();
  const [passwordAuthed, setPasswordAuthed] = useState(false);
  const [password, setPassword] = useState('');
  const [showPasswordFallback, setShowPasswordFallback] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('emails'); // 'emails', 'content', 'json'

  const DEFAULT_ADMINS = [
    'hailemariameyayu@gmail.com',
    'hailemariameyayu2012@gmail.com'
  ];

  // Check if current user is an admin by email, role, or isAdmin flag
  const isEmailAdmin = Boolean(
    user?.isAdmin ||
    user?.role === 'admin' ||
    user?.isAllowed ||
    (user?.email && DEFAULT_ADMINS.includes(user.email.toLowerCase().trim()))
  );

  const isAuthenticated = isEmailAdmin || passwordAuthed;

  // Admin password
  const ADMIN_PASSWORD = 'gitsawe2024admin';
  const apiURL = process.env.REACT_APP_API_URL || '/api';

  // Allowed Emails state
  const [emails, setEmails] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newRole, setNewRole] = useState('editor');
  const [canEdit, setCanEdit] = useState(true);
  const [canUpdate, setCanUpdate] = useState(true);
  const [canInsert, setCanInsert] = useState(true);
  const [canLike, setCanLike] = useState(true);
  const [editingEmailId, setEditingEmailId] = useState(null);

  // Content stats state
  const contentStats = {
    gitsawe: 366,
    sinksar: 366,
    mahlet: 22,
    wudasie: 12,
    seatat: 12,
    hibuat: 3,
    filseta: 2,
    total: 783
  };

  // JSON editor state
  const [selectedFile, setSelectedFile] = useState('');
  const [jsonData, setJsonData] = useState(null);
  const [editedData, setEditedData] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [isLoading, setIsLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [backupData, setBackupData] = useState(null);

  const jsonFiles = [
    { name: 'Gitsawe', path: 'gitsawe', description: 'ግጻዌ - 365 Daily Readings (MongoDB)' },
    { name: 'Sinksar', path: 'sinksar', description: 'ስንክሳር - 366 Saints & Martyrs (MongoDB)' },
    { name: 'Wudasie Mariam', path: 'wudasie', description: 'ውዳሴ ማርያም - 12 Praises of Mary (MongoDB)' },
    { name: 'Seatat', path: 'seatat', description: 'ሰአታት - 12 Canonical Hours (MongoDB)' },
    { name: 'Mahlet', path: 'mahlet', description: 'ማኅሌት - 22 Hymns (MongoDB)' },
    { name: 'Hibuat', path: 'hibuat', description: 'ኅቡዓት - 3 Secret Teachings & Prayers (MongoDB)' },
    { name: 'Filseta', path: 'filseta', description: 'ፍልሰታ - 2 Marian Hymns & Stanzas (MongoDB)' },
    { name: 'News', path: 'news', description: 'ዜናዎች - 20 Articles & Announcements (MongoDB)' },
  ];

  // Load allowed emails from database
  const loadAllowedEmails = useCallback(async () => {
    setLoadingEmails(true);
    try {
      const res = await fetch(`${apiURL}/admin-emails`);
      if (res.ok) {
        const data = await res.json();
        setEmails(data.emails || []);
      }
    } catch (err) {
      toast.error(`ኢሜይሎችን መጫን አልተቻለም: ${err.message}`);
    } finally {
      setLoadingEmails(false);
    }
  }, [apiURL]);

  useEffect(() => {
    if (isAuthenticated) {
      loadAllowedEmails();
    }
  }, [isAuthenticated, loadAllowedEmails]);

  const handleGoogleSignIn = async () => {
    setLoginLoading(true);
    try {
      await signInWithGoogle();
      toast.success('በGoogle በተሳካ ሁኔታ ገብተዋል');
    } catch (err) {
      toast.error(`የመግባት ስህተት: ${err.message}`);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogin = () => {
    if (password === ADMIN_PASSWORD) {
      setPasswordAuthed(true);
      toast.success('በተሳካ ሁኔታ እንደ አስተዳዳሪ ገብተዋል');
    } else {
      toast.error('ልክ ያልሆነ የይለፍ ቃል');
    }
  };

  // Add or update allowed email
  const handleSaveEmail = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) {
      toast.error('እባክዎ ትክክለኛ ኢሜይል ያስገቡ');
      return;
    }

    try {
      if (editingEmailId) {
        // Update
        const res = await fetch(`${apiURL}/admin-emails?id=${editingEmailId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: newEmail.trim().toLowerCase(),
            name: newName,
            role: newRole,
            canEdit,
            canUpdate,
            canInsert,
            canLike
          })
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.error || 'Update failed');
        }
        toast.success('የኢሜይል ፈቃዶች በተሳካ ሁኔታ ተሻሽለዋል');
      } else {
        // Create new
        const res = await fetch(`${apiURL}/admin-emails`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: newEmail.trim().toLowerCase(),
            name: newName,
            role: newRole,
            canEdit,
            canUpdate,
            canInsert,
            canLike,
            addedBy: user?.email || 'admin'
          })
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.error || 'Create failed');
        }
        toast.success('አዲስ ኢሜይል በተሳካ ሁኔታ ተፈቅዷል');
      }

      // Reset form
      setNewEmail('');
      setNewName('');
      setNewRole('editor');
      setCanEdit(true);
      setCanUpdate(true);
      setCanInsert(true);
      setCanLike(true);
      setEditingEmailId(null);
      loadAllowedEmails();
    } catch (err) {
      toast.error(`ስህተት: ${err.message}`);
    }
  };

  const handleEditEmail = (item) => {
    setEditingEmailId(item.id || item._id);
    setNewEmail(item.email);
    setNewName(item.name || '');
    setNewRole(item.role || 'editor');
    setCanEdit(item.canEdit !== false);
    setCanUpdate(item.canUpdate !== false);
    setCanInsert(item.canInsert !== false);
    setCanLike(item.canLike !== false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteEmail = async (id, email) => {
    if (!window.confirm(`እርግጠኛ ነዎት ${email} ከተፈቀዱ ኢሜይሎች ዝርዝር ይወገድ?`)) return;
    try {
      const res = await fetch(`${apiURL}/admin-emails?id=${id}`, {
        method: 'DELETE'
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Delete failed');
      }
      toast.success(`${email} ተወግዷል`);
      loadAllowedEmails();
    } catch (err) {
      toast.error(`የማስወገድ ስህተት: ${err.message}`);
    }
  };

  // JSON viewer logic
  const loadJsonFile = async (filePath) => {
    setIsLoading(true);
    try {
      const fileKey = filePath.split('/').pop().replace('.json', '').toLowerCase();
      const response = await fetch(`/api/admin-json?file=${fileKey}`, {
        headers: {
          'Authorization': `Bearer ${ADMIN_PASSWORD}`,
          'x-user-email': user?.email || ''
        }
      });
      if (!response.ok) throw new Error(`Failed to load ${filePath}: ${response.status}`);
      const data = await response.json();
      setJsonData(data.data);
      setBackupData(JSON.parse(JSON.stringify(data.data)));
      setEditedData(JSON.stringify(data.data, null, 2));
      toast.success(`Loaded ${filePath}`);
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const saveJsonFile = async () => {
    try {
      const parsedData = JSON.parse(editedData);
      const fileKey = selectedFile.split('/').pop().replace('.json', '').toLowerCase();
      if (jsonData) clientBackup.createBackup(fileKey, jsonData);

      const response = await fetch('/api/admin-json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${ADMIN_PASSWORD}`,
          'x-user-email': user?.email || ''
        },
        body: JSON.stringify({ file: fileKey, data: parsedData, userEmail: user?.email })
      });
      if (!response.ok) throw new Error('Save failed');
      setJsonData(parsedData);
      toast.success('JSON saved successfully!');
    } catch (err) {
      toast.error(`Error saving: ${err.message}`);
    }
  };

  if (authLoading) {
    return (
      <div className="admin-login-container">
        <div className="admin-login-box text-center p-8">
          <div className="animate-spin text-4xl mb-3">⏳</div>
          <p className="text-gray-600 font-medium">የመለያ ፈቃድ በማረጋገጥ ላይ...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="admin-login-container">
        <ToastContainer position="top-right" autoClose={3000} />
        <div className="admin-login-box">
          <div className="login-header">
            <div className="text-4xl mb-2">🔐</div>
            <h1>የአስተዳዳሪ ክፍል (Admin Dashboard)</h1>
            <p>Database & Allowed Emails Management</p>
          </div>

          {!user ? (
            <div className="p-6 text-center space-y-4">
              <p className="text-sm text-gray-600">
                ይህ ገጽ ለአስተዳዳሪዎች ብቻ የተፈቀደ ነው። እባክዎ በተፈቀደ የአስተዳዳሪ Google ኢሜይል ይግቡ።
              </p>
              <button
                onClick={handleGoogleSignIn}
                disabled={loginLoading}
                className="w-full flex items-center justify-center gap-3 px-5 py-3 bg-white border-2 border-amber-300 hover:border-amber-500 rounded-xl shadow-sm hover:shadow transition font-medium text-gray-800 disabled:opacity-50"
              >
                <svg width="20" height="20" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {loginLoading ? 'በመግባት ላይ...' : 'በGoogle ኢሜይል ይግቡ (Sign In)'}
              </button>

              <div className="pt-3 border-t border-gray-100 text-xs">
                <button
                  type="button"
                  onClick={() => setShowPasswordFallback(!showPasswordFallback)}
                  className="text-gray-400 hover:text-gray-600 underline"
                >
                  {showPasswordFallback ? 'የይለፍ ቃል ደብቅ' : 'ወይም በይለፍ ቃል (Fallback)'}
                </button>
                {showPasswordFallback && (
                  <div className="mt-3 flex gap-2">
                    <input
                      type="password"
                      placeholder="የይለፍ ቃል"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                      className="flex-1 px-3 py-1.5 text-xs border rounded-lg"
                    />
                    <button onClick={handleLogin} className="px-3 py-1.5 bg-gray-800 text-white rounded-lg text-xs">
                      ግባ
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="p-6 text-center space-y-4">
              <div className="text-3xl">⛔</div>
              <p className="text-sm font-semibold text-red-600">
                የመግቢያ ፈቃድ አልተሰጠዎትም (Access Denied)
              </p>
              <p className="text-xs text-gray-600">
                ያሁኑ መለያ <b>{user.email}</b> የአስተዳዳሪ ፈቃድ የለውም።
              </p>
              <div className="flex flex-col gap-2 pt-2">
                <button
                  onClick={handleGoogleSignIn}
                  className="w-full py-2.5 px-4 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-xl text-xs transition"
                >
                  🔁 በሌላ የአስተዳዳሪ መለያ ይግቡ
                </button>
                <button
                  onClick={logout}
                  className="w-full py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs transition"
                >
                  🚪 ውጣ (Logout)
                </button>
              </div>

              <div className="pt-3 border-t border-gray-100 text-xs">
                <button
                  type="button"
                  onClick={() => setShowPasswordFallback(!showPasswordFallback)}
                  className="text-gray-400 hover:text-gray-600 underline"
                >
                  {showPasswordFallback ? 'የይለፍ ቃል ደብቅ' : 'ወይም በይለፍ ቃል (Fallback)'}
                </button>
                {showPasswordFallback && (
                  <div className="mt-3 flex gap-2">
                    <input
                      type="password"
                      placeholder="የይለፍ ቃል"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                      className="flex-1 px-3 py-1.5 text-xs border rounded-lg"
                    />
                    <button onClick={handleLogin} className="px-3 py-1.5 bg-gray-800 text-white rounded-lg text-xs">
                      ግባ
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard max-w-7xl mx-auto p-4 sm:p-6">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900">
            ⚙️ የአስተዳዳሪ ክፍል (Admin Dashboard)
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            የተፈቀዱ ኢሜይሎች አስተዳደር፣ የሊተርጂካል ይዘቶች ዳታቤዝ እና የJSON መሣሪያዎች
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs px-3 py-1.5 bg-amber-100 text-amber-800 font-bold rounded-full">
            👤 {user?.email || 'Admin'}
          </span>
          <button
            onClick={() => {
              setPasswordAuthed(false);
              logout();
            }}
            className="px-4 py-2 text-xs font-semibold bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition"
          >
            🚪 ውጣ (Logout)
          </button>
        </div>
      </div>

      {/* Top Navigation Tabs */}
      <div className="flex gap-2 border-b border-gray-200 mb-6 pb-2">
        <button
          onClick={() => setActiveTab('emails')}
          className={`px-4 py-2 font-bold text-sm rounded-lg transition ${activeTab === 'emails' ? 'bg-blue-600 text-white shadow' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
        >
          📧 የተፈቀዱ ኢሜይሎች (Allowed Emails)
        </button>
        <button
          onClick={() => setActiveTab('content')}
          className={`px-4 py-2 font-bold text-sm rounded-lg transition ${activeTab === 'content' ? 'bg-blue-600 text-white shadow' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
        >
          ⚡ የዳታቤዝ ይዘቶች (Database Content)
        </button>
        <button
          onClick={() => setActiveTab('json')}
          className={`px-4 py-2 font-bold text-sm rounded-lg transition ${activeTab === 'json' ? 'bg-blue-600 text-white shadow' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
        >
          📁 JSON ፋይሎች (Legacy JSON Editor)
        </button>
      </div>

      {/* TAB 1: Allowed Emails Management */}
      {activeTab === 'emails' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Add / Edit Form */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span>{editingEmailId ? '✏️' : '➕'}</span>
              <span>{editingEmailId ? 'የኢሜይል ፈቃድ ማሻሻያ' : 'አዲስ የተፈቀደ ኢሜይል መመዝገቢያ'}</span>
            </h2>

            <form onSubmit={handleSaveEmail} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  ኢሜይል አድራሻ (Email) *
                </label>
                <input
                  type="email"
                  required
                  placeholder="example@gmail.com"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="w-full px-3 py-2 text-sm border rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  የተጠቃሚው ስም (Full Name)
                </label>
                <input
                  type="text"
                  placeholder="ለምሳሌ፡ መምህር / ዲያቆን..."
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="w-full px-3 py-2 text-sm border rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  ሚና (Role)
                </label>
                <select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  className="w-full px-3 py-2 text-sm border rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                >
                  <option value="admin">አስተዳዳሪ (Admin - Full Access)</option>
                  <option value="editor">አርታዒ (Editor - Edit & Insert)</option>
                  <option value="contributor">አበርካች (Contributor)</option>
                </select>
              </div>

              <div className="pt-2 border-t border-gray-100 space-y-2">
                <span className="block text-xs font-bold text-gray-700">ፈቃዶች (Permissions):</span>
                <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={canInsert}
                    onChange={(e) => setCanInsert(e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <span>አዲስ ይዘት ማስገባት (Insert New)</span>
                </label>
                <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={canEdit}
                    onChange={(e) => setCanEdit(e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <span>ይዘት ማረም (Edit Content)</span>
                </label>
                <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={canUpdate}
                    onChange={(e) => setCanUpdate(e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <span>ይዘት ማሻሻል (Update Content)</span>
                </label>
                <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={canLike}
                    onChange={(e) => setCanLike(e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <span>መውደድ እና ምልክት ማድረግ (Like & Interact)</span>
                </label>
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  type="submit"
                  className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition shadow"
                >
                  {editingEmailId ? '💾 ፈቃድ አሻሽል' : '➕ ኢሜይል ፍቀድ'}
                </button>
                {editingEmailId && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingEmailId(null);
                      setNewEmail('');
                      setNewName('');
                    }}
                    className="px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-xs"
                  >
                    ሰርዝ
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* List of Allowed Emails */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <span>📋</span>
                <span>የተፈቀዱ ኢሜይሎች ዝርዝር ({emails.length})</span>
              </h2>
              <button
                onClick={loadAllowedEmails}
                className="text-xs text-blue-600 hover:text-blue-800 font-semibold"
              >
                🔄 አድስ
              </button>
            </div>

            {loadingEmails ? (
              <p className="text-sm text-gray-500 py-6 text-center">እየተጫነ ነው...</p>
            ) : emails.length === 0 ? (
              <p className="text-sm text-gray-500 py-6 text-center">ምንም የተፈቀደ ኢሜይል አልተገኘም</p>
            ) : (
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {emails.map((item) => (
                  <div
                    key={item.id || item._id}
                    className="p-4 border border-gray-200 rounded-xl hover:border-blue-300 transition bg-gray-50/40 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-900 text-sm">{item.email}</span>
                        <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full ${item.role === 'admin' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'}`}>
                          {item.role || 'editor'}
                        </span>
                      </div>
                      {item.name && <p className="text-xs text-gray-600 mt-0.5">👤 {item.name}</p>}
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {item.canInsert && <span className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded border border-emerald-200">Insert</span>}
                        {item.canEdit && <span className="text-[10px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded border border-indigo-200">Edit</span>}
                        {item.canUpdate && <span className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded border border-blue-200">Update</span>}
                        {item.canLike && <span className="text-[10px] bg-rose-50 text-rose-700 px-1.5 py-0.5 rounded border border-rose-200">Like</span>}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 self-end sm:self-center">
                      <button
                        onClick={() => handleEditEmail(item)}
                        className="px-2.5 py-1 text-xs font-semibold bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-md transition"
                      >
                        ✏️ አርትዕ
                      </button>
                      <button
                        onClick={() => handleDeleteEmail(item.id || item._id, item.email)}
                        className="px-2.5 py-1 text-xs font-semibold bg-red-50 hover:bg-red-100 text-red-600 rounded-md transition"
                      >
                        🗑️ አስወግድ
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 2: Database Liturgical Content Status */}
      {activeTab === 'content' && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 space-y-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-gray-100 pb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                ⚡ የሊተርጂካል ይዘቶች በመረጃ ቋት (MongoDB) ውስጥ
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                ሁሉም የስንክሳር፣ ግጻዌ፣ ማኅሌት፣ ውዳሴ ማርያም፣ ሰዓታት፣ ኅቡዓት እና ፍልሰታ ይዘቶች ከJSON ወደ ዳታቤዝ ገብተዋል
              </p>
            </div>
            <span className="text-xs px-3 py-1.5 bg-emerald-100 text-emerald-800 font-bold rounded-full">
              ✅ ዳታቤዝ ገቢር ሆኗል (Database Active)
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-4 bg-amber-50 rounded-2xl border border-amber-200 flex flex-col justify-between">
              <div>
                <span className="text-3xl">📜</span>
                <div className="text-2xl font-black text-amber-900 mt-1">{contentStats.sinksar} ቀናት</div>
                <div className="text-xs font-bold text-amber-800">ስንክሳር (Sinksar)</div>
                <p className="text-[11px] text-gray-500 mt-1">የቅዱሳን ታሪክ እና መታሰቢያ</p>
              </div>
              <div className="flex gap-2 mt-3 pt-2 border-t border-amber-200">
                <button
                  onClick={() => { setSelectedFile('sinksar'); loadJsonFile('sinksar'); setActiveTab('json'); }}
                  className="flex-1 text-center py-1.5 px-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-semibold shadow"
                >
                  ✏️ አርትዕ (JSON)
                </button>
                <a href="/sinksar" className="py-1.5 px-3 bg-white hover:bg-amber-100 text-amber-800 border border-amber-300 rounded-lg text-xs font-semibold text-center">
                  ገጽ →
                </a>
              </div>
            </div>

            <div className="p-4 bg-blue-50 rounded-2xl border border-blue-200 flex flex-col justify-between">
              <div>
                <span className="text-3xl">📖</span>
                <div className="text-2xl font-black text-blue-900 mt-1">{contentStats.gitsawe} ቀናት</div>
                <div className="text-xs font-bold text-blue-800">ግጻዌ (Gitsawe)</div>
                <p className="text-[11px] text-gray-500 mt-1">የዓመቱ ዕለታዊ ምንባባት</p>
              </div>
              <div className="flex gap-2 mt-3 pt-2 border-t border-blue-200">
                <button
                  onClick={() => { setSelectedFile('gitsawe'); loadJsonFile('gitsawe'); setActiveTab('json'); }}
                  className="flex-1 text-center py-1.5 px-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold shadow"
                >
                  ✏️ አርትዕ (JSON)
                </button>
                <a href="/gitsawe" className="py-1.5 px-3 bg-white hover:bg-blue-100 text-blue-800 border border-blue-300 rounded-lg text-xs font-semibold text-center">
                  ገጽ →
                </a>
              </div>
            </div>

            <div className="p-4 bg-purple-50 rounded-2xl border border-purple-200 flex flex-col justify-between">
              <div>
                <span className="text-3xl">🎵</span>
                <div className="text-2xl font-black text-purple-900 mt-1">{contentStats.mahlet} መዝሙራት</div>
                <div className="text-xs font-bold text-purple-800">ማኅሌት (Mahlet)</div>
                <p className="text-[11px] text-gray-500 mt-1">የበዓላትና የአጽዋማት ሥርዓተ ማኅሌት</p>
              </div>
              <div className="flex gap-2 mt-3 pt-2 border-t border-purple-200">
                <button
                  onClick={() => { setSelectedFile('mahlet'); loadJsonFile('mahlet'); setActiveTab('json'); }}
                  className="flex-1 text-center py-1.5 px-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs font-semibold shadow"
                >
                  ✏️ አርትዕ (JSON)
                </button>
                <a href="/mahlet" className="py-1.5 px-3 bg-white hover:bg-purple-100 text-purple-800 border border-purple-300 rounded-lg text-xs font-semibold text-center">
                  ገጽ →
                </a>
              </div>
            </div>

            <div className="p-4 bg-rose-50 rounded-2xl border border-rose-200 flex flex-col justify-between">
              <div>
                <span className="text-3xl">🙏</span>
                <div className="text-2xl font-black text-rose-900 mt-1">{contentStats.wudasie} ክፍሎች</div>
                <div className="text-xs font-bold text-rose-800">ውዳሴ ማርያም (Wudasie)</div>
                <p className="text-[11px] text-gray-500 mt-1">የሰባቱ ዕለታት ውዳሴና አንቀጸ ብርሃን</p>
              </div>
              <div className="flex gap-2 mt-3 pt-2 border-t border-rose-200">
                <button
                  onClick={() => { setSelectedFile('wudasie'); loadJsonFile('wudasie'); setActiveTab('json'); }}
                  className="flex-1 text-center py-1.5 px-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-xs font-semibold shadow"
                >
                  ✏️ አርትዕ (JSON)
                </button>
                <a href="/wudasie-mariam" className="py-1.5 px-3 bg-white hover:bg-rose-100 text-rose-800 border border-rose-300 rounded-lg text-xs font-semibold text-center">
                  ገጽ →
                </a>
              </div>
            </div>

            <div className="p-4 bg-cyan-50 rounded-2xl border border-cyan-200 flex flex-col justify-between">
              <div>
                <span className="text-3xl">⏰</span>
                <div className="text-2xl font-black text-cyan-900 mt-1">{contentStats.seatat} ክፍሎች</div>
                <div className="text-xs font-bold text-cyan-800">መጽሐፈ ሰዓታት (Seatat)</div>
                <p className="text-[11px] text-gray-500 mt-1">የሰዓታዊ ጸሎቶች ሥርዓት</p>
              </div>
              <div className="flex gap-2 mt-3 pt-2 border-t border-cyan-200">
                <button
                  onClick={() => { setSelectedFile('seatat'); loadJsonFile('seatat'); setActiveTab('json'); }}
                  className="flex-1 text-center py-1.5 px-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-xs font-semibold shadow"
                >
                  ✏️ አርትዕ (JSON)
                </button>
                <a href="/seatat" className="py-1.5 px-3 bg-white hover:bg-cyan-100 text-cyan-800 border border-cyan-300 rounded-lg text-xs font-semibold text-center">
                  ገጽ →
                </a>
              </div>
            </div>

            <div className="p-4 bg-emerald-50 rounded-2xl border border-emerald-200 flex flex-col justify-between">
              <div>
                <span className="text-3xl">📜</span>
                <div className="text-2xl font-black text-emerald-900 mt-1">{contentStats.hibuat} ክፍሎች</div>
                <div className="text-xs font-bold text-emerald-800">መጽሐፈ ኅቡዓት (Hibuat)</div>
                <p className="text-[11px] text-gray-500 mt-1">የሚስጥራዊ ትምህርቶችና ጸሎቶች</p>
              </div>
              <div className="flex gap-2 mt-3 pt-2 border-t border-emerald-200">
                <button
                  onClick={() => { setSelectedFile('hibuat'); loadJsonFile('hibuat'); setActiveTab('json'); }}
                  className="flex-1 text-center py-1.5 px-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold shadow"
                >
                  ✏️ አርትዕ (JSON)
                </button>
                <a href="/hibuat" className="py-1.5 px-3 bg-white hover:bg-emerald-100 text-emerald-800 border border-emerald-300 rounded-lg text-xs font-semibold text-center">
                  ገጽ →
                </a>
              </div>
            </div>

            <div className="p-4 bg-pink-50 rounded-2xl border border-pink-200 flex flex-col justify-between">
              <div>
                <span className="text-3xl">🌸</span>
                <div className="text-2xl font-black text-pink-900 mt-1">{contentStats.filseta} ክፍሎች</div>
                <div className="text-xs font-bold text-pink-800">ፍልሰታና ማኅሌተ ጽጌ (Filseta)</div>
                <p className="text-[11px] text-gray-500 mt-1">የእመቤታችን የጾመ ፍልሰታ መዝሙራት</p>
              </div>
              <div className="flex gap-2 mt-3 pt-2 border-t border-pink-200">
                <button
                  onClick={() => { setSelectedFile('filseta'); loadJsonFile('filseta'); setActiveTab('json'); }}
                  className="flex-1 text-center py-1.5 px-2 bg-pink-600 hover:bg-pink-700 text-white rounded-lg text-xs font-semibold shadow"
                >
                  ✏️ አርትዕ (JSON)
                </button>
                <a href="/filseta" className="py-1.5 px-3 bg-white hover:bg-pink-100 text-pink-800 border border-pink-300 rounded-lg text-xs font-semibold text-center">
                  ገጽ →
                </a>
              </div>
            </div>

            <div className="p-4 bg-gray-50 rounded-2xl border border-gray-200 flex flex-col justify-between">
              <div>
                <span className="text-3xl">📰</span>
                <div className="text-2xl font-black text-gray-900 mt-1">20 ዜናዎች</div>
                <div className="text-xs font-bold text-gray-800">ዜናዎችና ማስታወቂያዎች (News)</div>
                <p className="text-[11px] text-gray-500 mt-1">አንቀጾችና የቤተ ክርስቲያን ዜናዎች</p>
              </div>
              <div className="flex gap-2 mt-3 pt-2 border-t border-gray-200">
                <button
                  onClick={() => { setSelectedFile('news'); loadJsonFile('news'); setActiveTab('json'); }}
                  className="flex-1 text-center py-1.5 px-2 bg-gray-700 hover:bg-gray-800 text-white rounded-lg text-xs font-semibold shadow"
                >
                  ✏️ አርትዕ (JSON)
                </button>
                <a href="/news" className="py-1.5 px-3 bg-white hover:bg-gray-100 text-gray-800 border border-gray-300 rounded-lg text-xs font-semibold text-center">
                  ገጽ →
                </a>
              </div>
            </div>
          </div>

          <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
            <h3 className="text-sm font-bold text-gray-800 mb-2">📌 የስርዓቱ ጠቀሜታ፡</h3>
            <ul className="text-xs text-gray-600 space-y-1 list-disc list-inside">
              <li>ይዘቶች ከትልቅ 6.5MB JSON ፋይሎች ይልቅ በቀጥታ ከMongoDB API በቅጽበት ይጫናሉ</li>
              <li>የተፈቀዱ ኢሜይሎች በቀጥታ በገጾቹ ላይ ሆነው ይዘቱን ማረም (Edit)፣ ማሻሻል (Update) እና አዲስ ማስገባት (Insert New) ይችላሉ</li>
              <li>የቀን ንባቦችና ታሪኮች በመውደድ (Like) እና በምልክት (Bookmark) ከዳታቤዝ ጋር የተቆራኙ ናቸው</li>
            </ul>
          </div>
        </div>
      )}

      {/* TAB 3: Legacy JSON Editor */}
      {activeTab === 'json' && (
        <div className="admin-content grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="file-sidebar bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <h2 className="font-bold text-sm mb-3">📁 JSON Files</h2>
            <div className="file-list space-y-2">
              {jsonFiles.map((file) => (
                <div
                  key={file.path}
                  className={`p-2.5 rounded-lg border cursor-pointer transition text-xs ${selectedFile === file.path ? 'bg-blue-50 border-blue-400 font-bold' : 'border-gray-200 hover:bg-gray-50'}`}
                  onClick={() => {
                    setSelectedFile(file.path);
                    loadJsonFile(file.path);
                  }}
                >
                  <div>📄 {file.name}</div>
                  <div className="text-[10px] text-gray-500">{file.description}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="editor-main md:col-span-3 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            {selectedFile ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-sm">✏️ {selectedFile}</h3>
                  <button onClick={saveJsonFile} className="px-4 py-1.5 bg-blue-600 text-white rounded text-xs font-bold">
                    💾 Save File
                  </button>
                </div>
                <textarea
                  rows={20}
                  value={editedData}
                  onChange={(e) => setEditedData(e.target.value)}
                  className="w-full font-mono text-xs p-3 border rounded-lg border-gray-300 outline-none"
                />
              </div>
            ) : (
              <div className="text-center py-20 text-gray-400 text-sm">
                ፋይል ይምረጡ
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
